import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { ChevronDown, ChevronRight, Folder, FolderTree, Loader2, Lock, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import * as Query from '../../../../api/gql/query'
import * as Mutation from '../../../../api/gql/mutation'
import FolderAccessDialogSkeleton from './FolderAccessDialogSkeleton'

const LEVELS = ['VIEWER', 'COMMENTER', 'EDITOR', 'MANAGER', 'ADMIN'] as const

// Tailwind cannot see dynamically-built class names like `pl-${n}` — the
// JIT compiler only picks up classes it can find as literal strings in the
// source, so a fixed ladder map is required instead of string interpolation.
const INDENT = ['pl-0', 'pl-4', 'pl-8', 'pl-12', 'pl-16']

interface Props {
  open: boolean
  projectId: string
  member: { id: string; name: string | null } | null
  onClose: () => void
}

interface Node {
  folderId: string
  name: string
  parentId: string | null
  restricted: boolean
  level: string | null
  source: string | null
  directLevel: string | null
}

const FolderAccessDialog: React.FC<Props> = ({ open, projectId, member, onClose }) => {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(Query.USER_FOLDER_ACCESS_TREE, {
    variables: { projectId, userId: member?.id },
    skip: !open || !member,
    fetchPolicy: 'network-only',
  })
  const [grant] = useMutation(Mutation.GRANT_DOC_FOLDER_PERMISSION)
  const [revoke] = useMutation(Mutation.REVOKE_DOC_FOLDER_PERMISSION)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')

  const nodes: Node[] = data?.userFolderAccessTree ?? []

  // Diacritics-insensitive match so "rez" finds "Řez podlaží"
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  // While searching: keep matches plus all their ancestors so the tree shape
  // stays readable; collapse state is ignored (results are force-expanded).
  const filterSet = useMemo(() => {
    const q = norm(query.trim())
    if (!q) return null
    const byId = new Map(nodes.map((n) => [n.folderId, n]))
    const keep = new Set<string>()
    for (const n of nodes) {
      if (!norm(n.name).includes(q)) continue
      let cur: Node | undefined = n
      while (cur && !keep.has(cur.folderId)) {
        keep.add(cur.folderId)
        cur = cur.parentId ? byId.get(cur.parentId) : undefined
      }
    }
    return keep
  }, [nodes, query])

  const { ordered, parentIds } = useMemo(() => {
    // depth-first flatten so children render under parents with indentation
    const byParent = new Map<string | null, Node[]>()
    for (const n of nodes) {
      const list = byParent.get(n.parentId) ?? []
      list.push(n)
      byParent.set(n.parentId, list)
    }
    const parentIds = new Set(
      nodes.filter((n) => (byParent.get(n.folderId) ?? []).length > 0).map((n) => n.folderId),
    )
    const out: Array<Node & { depth: number }> = []
    // visited guards against a corrupt/cyclic parentId chain recursing forever
    const visited = new Set<string>()
    const walk = (parentId: string | null, depth: number) => {
      for (const n of byParent.get(parentId) ?? []) {
        if (visited.has(n.folderId)) continue
        visited.add(n.folderId)
        if (filterSet && !filterSet.has(n.folderId)) continue
        out.push({ ...n, depth })
        if (!filterSet && collapsed.has(n.folderId)) continue
        walk(n.folderId, depth + 1)
      }
    }
    walk(null, 0)
    return { ordered: out, parentIds }
  }, [nodes, filterSet, collapsed])

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const levelName = (l: string) => t('DocControl_role_' + l.toLowerCase(), l)
  // Suffix explaining WHERE a non-direct effective level comes from. 'grant'
  // with no directLevel = a role-grant row on this exact folder.
  const sourceSuffix = (n: Node) => {
    switch (n.source) {
      case 'implicit': return t('Members_folder_source_implicit', 'module rights')
      case 'inherited': return t('Members_folder_source_inherited', 'inherited')
      case 'admin': return t('Members_folder_source_admin', 'module admin')
      case 'grant': return n.directLevel ? null : t('Members_folder_source_role', 'role')
      default: return null
    }
  }
  // The closed select must show the member's EFFECTIVE access, not just the
  // explicit grant — "No access" while the module axis grants implicit access
  // is exactly the confusion of Freelo #31448982.
  const effectiveLabel = (n: Node) => {
    if (n.directLevel) return levelName(n.directLevel)
    if (n.level) {
      const suffix = sourceSuffix(n)
      return suffix ? `${levelName(n.level)} · ${suffix}` : levelName(n.level)
    }
    return t('Members_folder_no_access', 'No access')
  }

  const setLevel = async (node: Node, value: string) => {
    if (!member) return
    setPendingId(node.folderId)
    try {
      if (value === 'NONE') {
        await revoke({ variables: { folderId: node.folderId, userId: member.id } })
      } else {
        await grant({ variables: { folderId: node.folderId, userId: member.id, level: value } })
      }
      await refetch()
    } catch (e: any) {
      toastr.error(t('error', 'Error'), e.message)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderTree className="h-4 w-4" />
            {t('Members_folder_access_title', 'Document folder access')}{member && <> — {member.name}</>}
          </DialogTitle>
        </DialogHeader>
        <div className="relative shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('Common_search', 'Search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        {loading && !data ? <FolderAccessDialogSkeleton /> : (
          // p-1.5/-m-1.5: overflow-y-auto also clips horizontally, so the
          // scroll box needs interior room for the 4px focus ring; the negative
          // margin keeps rows aligned with the dialog padding (replaces the old
          // pr-1 scrollbar gap — 6px right padding covers that too).
          <div className="space-y-1 flex-1 min-h-0 overflow-y-auto p-1.5 -m-1.5">
            {ordered.map((n) => (
              <div key={n.folderId} className="flex items-center gap-2">
                <div className={cn('flex items-center gap-1.5 flex-1 min-w-0', INDENT[Math.min(n.depth, 4)])}>
                  {parentIds.has(n.folderId) && !filterSet ? (
                    <button
                      type="button"
                      onClick={() => toggleCollapsed(n.folderId)}
                      className="h-4 w-4 shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label={collapsed.has(n.folderId) ? t('Common_expand', 'Expand') : t('Common_collapse', 'Collapse')}
                    >
                      {collapsed.has(n.folderId)
                        ? <ChevronRight className="h-4 w-4" />
                        : <ChevronDown className="h-4 w-4" />}
                    </button>
                  ) : (
                    <span className="w-4 shrink-0" />
                  )}
                  <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="truncate text-sm">{n.name}</span>
                  {n.restricted && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                </div>
                {pendingId === n.folderId ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Select
                    value={n.directLevel ?? 'NONE'}
                    onValueChange={(v) => setLevel(n, v)}
                  >
                    <SelectTrigger className={cn('h-7 w-[170px] text-xs', !n.directLevel && 'text-muted-foreground')}>
                      <SelectValue>{effectiveLabel(n)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">{t('Members_folder_option_default', 'Default (no direct grant)')}</SelectItem>
                      {LEVELS.map((l) => (
                        <SelectItem key={l} value={l}>{t('DocControl_role_' + l.toLowerCase(), l)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
            {ordered.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {query.trim()
                  ? t('Members_folder_no_match', 'No folders match your search.')
                  : t('Members_folder_empty', 'This project has no folders yet.')}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default FolderAccessDialog
